import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import { Calculator, Plus, Trash2, ArrowDown, ArrowUp, Loader2, ChevronDown, ChevronRight, Download } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

const formatMonth = (yyyyMm) => {
    try {
        const [y, m] = yyyyMm.split('-');
        return format(new Date(Number(y), Number(m) - 1, 1), 'MMMM yyyy', { locale: fr });
    } catch { return yyyyMm; }
};

const formatEuro = (n) => (Number(n) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

// Dialog : créer un calcul mensuel (choix du mois)
function CreateCalculDialog({ open, onOpenChange, demenageurId, existingMonths, onCreated }) {
    const [month, setMonth] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open) {
            // Par défaut : mois en cours
            const d = new Date();
            setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
        }
    }, [open]);

    const submit = async () => {
        if (!month) return toast.error('Choisir un mois');
        if (existingMonths.includes(month)) return toast.error(`Un calcul existe déjà pour ${formatMonth(month)}`);
        setSubmitting(true);
        try {
            await axios.post(`${API}/api/exploitation/demenageurs/${demenageurId}/calculs`, { month }, { withCredentials: true });
            toast.success('Calcul mensuel créé');
            onCreated();
            onOpenChange(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        } finally { setSubmitting(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md" data-testid="create-calcul-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Calculator size={18} className="text-[#2e7cd6]" />
                        Nouveau calcul mensuel
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-sm">Mois</Label>
                        <Input type="month" value={month} onChange={e => setMonth(e.target.value)} data-testid="calcul-month" />
                        {month && <p className="text-xs text-slate-500 mt-1">→ {formatMonth(month)}</p>}
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Annuler</Button>
                    <Button onClick={submit} disabled={submitting} className="bg-[#2e7cd6] hover:bg-[#2568b5] text-white" data-testid="create-calcul-submit">
                        {submitting ? <><Loader2 size={14} className="mr-1 animate-spin" /> …</> : 'Créer'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// Dialog : ajouter un paiement ou une dépense
function AddItemDialog({ open, onOpenChange, type, calcId, onAdded }) {
    const [label, setLabel] = useState('');
    const [montant, setMontant] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => { if (open) { setLabel(''); setMontant(''); } }, [open]);

    const submit = async () => {
        if (!label.trim()) return toast.error('Libellé obligatoire');
        if (montant === '' || Number(montant) < 0) return toast.error('Montant invalide');
        setSubmitting(true);
        const endpoint = type === 'paiement' ? 'paiement' : 'depense';
        try {
            await axios.post(
                `${API}/api/exploitation/calculs/${calcId}/${endpoint}`,
                { label: label.trim(), montant: Number(montant) },
                { withCredentials: true },
            );
            toast.success(type === 'paiement' ? 'Paiement ajouté' : 'Dépense ajoutée');
            onAdded();
            onOpenChange(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        } finally { setSubmitting(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md" data-testid={`add-${type}-dialog`}>
                <DialogHeader>
                    <DialogTitle>
                        {type === 'paiement' ? 'Ajouter un paiement effectué' : 'Ajouter une dépense supplémentaire'}
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-sm">Libellé</Label>
                        <Input value={label} onChange={e => setLabel(e.target.value)} placeholder={type === 'paiement' ? 'Acompte, Salaire, ...' : 'Prime, Bonus, ...'} data-testid={`${type}-label`} />
                    </div>
                    <div>
                        <Label className="text-sm">Montant (€)</Label>
                        <Input type="number" step="0.01" min="0" value={montant} onChange={e => setMontant(e.target.value)} data-testid={`${type}-montant`} />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Annuler</Button>
                    <Button onClick={submit} disabled={submitting} className={type === 'paiement' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-amber-500 hover:bg-amber-600 text-white'} data-testid={`${type}-submit`}>
                        {submitting ? <Loader2 size={14} className="animate-spin" /> : 'Ajouter'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default function CalculMensuelSection({ demenageurId }) {
    const [calculs, setCalculs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedId, setExpandedId] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [addItem, setAddItem] = useState({ open: false, type: '', calcId: '' });

    const fetch = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/api/exploitation/demenageurs/${demenageurId}/calculs`, { withCredentials: true });
            setCalculs(data || []);
        } catch { setCalculs([]); }
        finally { setLoading(false); }
    }, [demenageurId]);

    useEffect(() => { if (demenageurId) fetch(); }, [demenageurId, fetch]);

    const deleteCalcul = async (id) => {
        if (!window.confirm('Supprimer ce calcul mensuel ?')) return;
        try {
            await axios.delete(`${API}/api/exploitation/calculs/${id}`, { withCredentials: true });
            toast.success('Calcul supprimé');
            fetch();
        } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    };

    const removeItem = async (calcId, type, itemId) => {
        const endpoint = type === 'paiement' ? 'paiement' : 'depense';
        try {
            await axios.delete(`${API}/api/exploitation/calculs/${calcId}/${endpoint}/${itemId}`, { withCredentials: true });
            toast.success('Retiré');
            fetch();
        } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    };

    const downloadPdf = async (calc) => {
        try {
            const res = await axios.get(
                `${API}/api/exploitation/calculs/${calc.id}/pdf`,
                { withCredentials: true, responseType: 'blob' },
            );
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Calcul-mensuel-${calc.month}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success('PDF téléchargé');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors du téléchargement du PDF');
        }
    };

    return (
        <Card className="bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="calcul-mensuel-section">
            <CardContent className="p-5">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wide" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Calculs mensuels
                    </h2>
                    <Button
                        size="sm"
                        onClick={() => setCreateOpen(true)}
                        className="bg-[#2e7cd6] hover:bg-[#2568b5] text-white"
                        data-testid="new-calcul-btn"
                    >
                        <Plus size={14} className="mr-1" /> Créer un calcul mensuel
                    </Button>
                </div>

                {loading ? (
                    <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
                ) : calculs.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-6">Aucun calcul mensuel créé.</p>
                ) : (
                    <div className="space-y-3">
                        {calculs.map((c) => {
                            const isOpen = expandedId === c.id;
                            return (
                                <div key={c.id} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`calcul-${c.id}`}>
                                    {/* Header */}
                                    <div
                                        className="flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors"
                                        onClick={() => setExpandedId(isOpen ? null : c.id)}
                                    >
                                        <div className="flex items-center gap-3">
                                            {isOpen ? <ChevronDown size={16} className="text-slate-500" /> : <ChevronRight size={16} className="text-slate-500" />}
                                            <Calculator size={16} className="text-[#2e7cd6]" />
                                            <div>
                                                <p className="text-sm font-semibold text-slate-900 capitalize">Calcul {formatMonth(c.month)}</p>
                                                <p className="text-xs text-slate-500">Base {formatEuro(c.solde_base)} • {c.total.nb_jour}j × {c.tarif_jour.toFixed(2)}€ + {c.total.panier}×25€ + {c.total.autres.toFixed(2)}€</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="text-right">
                                                <p className="text-xs text-slate-500 uppercase">Solde final</p>
                                                <p className={`text-lg font-bold ${c.solde_final >= 0 ? 'text-[#2e7cd6]' : 'text-red-600'}`}>
                                                    {formatEuro(c.solde_final)}
                                                </p>
                                            </div>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); downloadPdf(c); }}
                                                className="p-2 text-slate-500 hover:text-[#2e7cd6] rounded hover:bg-white transition-colors"
                                                title="Télécharger le PDF"
                                                data-testid={`download-calcul-pdf-${c.id}`}
                                            >
                                                <Download size={14} />
                                            </button>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); deleteCalcul(c.id); }}
                                                className="p-2 text-slate-400 hover:text-red-500 rounded hover:bg-white"
                                                title="Supprimer le calcul"
                                                data-testid={`delete-calcul-${c.id}`}
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Body */}
                                    {isOpen && (
                                        <div className="p-4 space-y-4">
                                            {/* Récapitulatif */}
                                            <div className="grid grid-cols-3 gap-3">
                                                <div className="rounded-md border border-slate-200 bg-white p-3">
                                                    <p className="text-xs text-slate-500 uppercase tracking-wide">Base pointage</p>
                                                    <p className="text-lg font-bold text-slate-900">{formatEuro(c.solde_base)}</p>
                                                </div>
                                                <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                                                    <p className="text-xs text-amber-700 uppercase tracking-wide flex items-center gap-1"><ArrowUp size={11} /> Dépenses (+)</p>
                                                    <p className="text-lg font-bold text-amber-700">{formatEuro(c.sum_depenses)}</p>
                                                </div>
                                                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
                                                    <p className="text-xs text-emerald-700 uppercase tracking-wide flex items-center gap-1"><ArrowDown size={11} /> Paiements (−)</p>
                                                    <p className="text-lg font-bold text-emerald-700">{formatEuro(c.sum_paiements)}</p>
                                                </div>
                                            </div>

                                            {/* Listes côte à côte */}
                                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                                {/* Paiements */}
                                                <div className="border border-slate-200 rounded-md">
                                                    <div className="flex items-center justify-between px-3 py-2 bg-emerald-50 border-b border-slate-200">
                                                        <p className="text-xs font-semibold text-emerald-700 uppercase flex items-center gap-1">
                                                            <ArrowDown size={12} /> Paiements effectués
                                                        </p>
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => setAddItem({ open: true, type: 'paiement', calcId: c.id })}
                                                            className="h-7 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                                                            data-testid={`add-paiement-btn-${c.id}`}
                                                        >
                                                            <Plus size={12} className="mr-1" /> Ajouter
                                                        </Button>
                                                    </div>
                                                    <div className="divide-y divide-slate-100">
                                                        {c.paiements.length === 0 ? (
                                                            <p className="text-xs text-slate-400 italic text-center py-3">Aucun paiement</p>
                                                        ) : c.paiements.map((p) => (
                                                            <div key={p.id} className="flex items-center justify-between px-3 py-2">
                                                                <div className="min-w-0">
                                                                    <p className="text-sm text-slate-900 truncate">{p.label}</p>
                                                                    <p className="text-xs text-slate-500">{p.date}</p>
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">− {formatEuro(p.montant)}</Badge>
                                                                    <button onClick={() => removeItem(c.id, 'paiement', p.id)} className="p-1 text-slate-300 hover:text-red-500" title="Retirer"><Trash2 size={12} /></button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>

                                                {/* Dépenses */}
                                                <div className="border border-slate-200 rounded-md">
                                                    <div className="flex items-center justify-between px-3 py-2 bg-amber-50 border-b border-slate-200">
                                                        <p className="text-xs font-semibold text-amber-700 uppercase flex items-center gap-1">
                                                            <ArrowUp size={12} /> Dépenses supplémentaires
                                                        </p>
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => setAddItem({ open: true, type: 'depense', calcId: c.id })}
                                                            className="h-7 text-xs border-amber-300 text-amber-700 hover:bg-amber-100"
                                                            data-testid={`add-depense-btn-${c.id}`}
                                                        >
                                                            <Plus size={12} className="mr-1" /> Ajouter
                                                        </Button>
                                                    </div>
                                                    <div className="divide-y divide-slate-100">
                                                        {c.depenses.length === 0 ? (
                                                            <p className="text-xs text-slate-400 italic text-center py-3">Aucune dépense</p>
                                                        ) : c.depenses.map((d) => (
                                                            <div key={d.id} className="flex items-center justify-between px-3 py-2">
                                                                <div className="min-w-0">
                                                                    <p className="text-sm text-slate-900 truncate">{d.label}</p>
                                                                    <p className="text-xs text-slate-500">{d.date}</p>
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">+ {formatEuro(d.montant)}</Badge>
                                                                    <button onClick={() => removeItem(c.id, 'depense', d.id)} className="p-1 text-slate-300 hover:text-red-500" title="Retirer"><Trash2 size={12} /></button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Formule */}
                                            <div className="text-xs text-slate-500 italic bg-slate-50 rounded px-3 py-2">
                                                {formatEuro(c.solde_base)} + {formatEuro(c.sum_depenses)} (dépenses) − {formatEuro(c.sum_paiements)} (paiements) = <strong className="text-slate-900 not-italic">{formatEuro(c.solde_final)}</strong>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </CardContent>

            <CreateCalculDialog
                open={createOpen}
                onOpenChange={setCreateOpen}
                demenageurId={demenageurId}
                existingMonths={calculs.map(c => c.month)}
                onCreated={fetch}
            />
            <AddItemDialog
                open={addItem.open}
                onOpenChange={(o) => setAddItem(prev => ({ ...prev, open: o }))}
                type={addItem.type}
                calcId={addItem.calcId}
                onAdded={fetch}
            />
        </Card>
    );
}
